// src/components/ThemeProviderWrapper.tsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import { CssBaseline, PaletteMode } from "@mui/material";
import { muiLightTheme } from "../material-ui/MuiLightTheme";
import { muiDarkTheme } from "../material-ui/MuiDarkTheme";
import { PrimeReactProvider } from "primereact/api";
import "primereact/resources/primereact.min.css"; // PrimeReact core CSS
import "primeicons/primeicons.css"; // PrimeIcons
import useLocalStorageChange from "../../hooks/useLocalStorageChange";

const darkThemeUrlPrimeReact =
  "https://unpkg.com/primereact/resources/themes/md-dark-indigo/theme.css";

/**
 * ThemeProviderWrapper component is a wrapper component that applies the theme to the application.
 * It uses the localStorage to get the current theme value and applies the theme to the application.
 * It also applies the theme to the @forge/common components.
 * It also applies the theme to the mui components.
 * @param children - The children components to be wrapped
 *
 */
const ThemeProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const [theme, setCurrentTheme] = useState(
    localStorage.getItem("currentTheme") || "light"
  );
  const themeClass = theme === "light" ? "light-theme" : "dark-theme";

  // MUI Theme
  const muiTheme = theme === "light" ? muiLightTheme : muiDarkTheme;

  useLocalStorageChange("currentTheme", (updatedTheme) => {
    if (updatedTheme) {
      setCurrentTheme(updatedTheme);
    }
  });

  /**
   *
   * Apply theme for the @forge/common components
   */
  useEffect(() => {
    document.body.classList.add(theme); // for @forge/common
    document.body.classList.add(themeClass); //for custom html elements
    // Cleanup function to remove the class when the component re-renders or unmounts
    return () => {
      document.body.classList.remove(theme);
      document.body.classList.remove(themeClass);
    };
  }, [theme]); // Dependency array to re-run the effect when the theme changes

  /**PrimeReact Theme */
  useEffect(() => {
    if (theme === "dark") {
      const linkElement = document.createElement("link");
      linkElement.rel = "stylesheet";
      linkElement.href = darkThemeUrlPrimeReact;
      document.head.appendChild(linkElement);

      return () => {
        document.head.removeChild(linkElement);
      };
    }
  }, [theme]);

  return (
    <MUIThemeProvider theme={muiTheme}>
      <CssBaseline />
      <PrimeReactProvider>
        {/* Wrap children with Forge/Common ThemeProvider if necessary */}
        {children}
      </PrimeReactProvider>
    </MUIThemeProvider>
  );
};

export default ThemeProviderWrapper;
